import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import {
    AlertTypes,
    DataGridPro,
    Leap4Breadcrumbs,
    Mui,
    MuiDataGrid,
    MuiIcons,
    setAlert,
    Spinner,
} from '@leap4/frontend';
import { urlService } from '../../routing/urlsService';
import { dateToStringFormat } from '../../utils/functions';
import { ICheckResult, ITrackedLine, IUpdateList, resolveAppAdsTxtUrl } from './appAdsLines.interface';
import {
    checkPublisher,
    checkPublisherFromText,
    createLine,
    downloadUpdateListCsv,
    fetchLines,
    fetchPublisher,
    fetchUpdateList,
    importLinesFromCsv,
    importLinesFromText,
    massDeleteFromCsv,
    massDeleteFromText,
    previewDeleteCsv,
    previewDeleteText,
    previewLinesCsv,
    previewLinesText,
    purgeRemovedLines,
    setLineRemoved,
    updateLine,
    errorMessage,
} from './appAdsLines.api';
import { RemovalStatusChip, StatusChip } from './statusChip';
import {
    LineFormModal,
    LineHistoryModal,
    ManualCheckModal,
    PasteLinesModal,
    UpdateListModal,
    UploadRowsModal,
} from './lineModals';
import { describeSaveResult, GenerateLinesModal } from './generateLinesModal';
import { AppAdsLinesTabs } from './appAdsLinesTabs';
import { BackButton } from './backButton';
import { useWrappingGrid } from './wrappingGrid';
import { FiltersToolbar } from './filtersToolbar';

type GridColDef = MuiDataGrid.GridColDef;

const {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} = Mui;
const {
    Add: AddIcon,
    Remove: RemoveIcon,
    Refresh: RefreshIcon,
    History: HistoryIcon,
    Delete: DeleteIcon,
    Edit: EditIcon,
    OpenInNewOutlined: OpenInNewIcon,
    Download: DownloadIcon,
    Toc: TocIcon,
} = MuiIcons;

const connector = connect(() => ({}), { setAlert });
type PropsFromRedux = ConnectedProps<typeof connector>;

/**
 * One publisher: their tracked lines, the removal requests outstanding against them, and a log
 * of what's changed.
 *
 * Three tabs rather than four - generation lives in the "+ Add lines" menu, because it's a
 * fourth way lines get in (alongside typing, pasting and uploading) rather than a peer of
 * "Old lines".
 *
 * Naming follows the original ad-ops tool: "Old lines" rather than "removal requests",
 * "Delete lines by list" rather than "bulk remove". Ad-ops already have words for these
 * things, and inventing new ones just makes the screen harder to talk about.
 */
const TrackedPublisherDetail: React.FC<PropsFromRedux> = ({ setAlert }) => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const queryClient = useQueryClient();

    const [tab, setTab] = useState(0);
    const lineWrapping = useWrappingGrid();
    const removedWrapping = useWrappingGrid();
    const activityWrapping = useWrappingGrid();
    const [oldSelection, setOldSelection] = useState<{ publisherId: string; ids: string[] }>({ publisherId: id, ids: [] });
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ publisherId: string; ids: string[] } | null>(null);
    const selectedOldIds = oldSelection.publisherId === id ? oldSelection.ids : [];
    useEffect(() => {
        setOldSelection({ publisherId: id, ids: [] });
        setDeleteConfirmation(null);
    }, [id]);
    const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
    const [lineFormOpen, setLineFormOpen] = useState(false);
    const [editingLine, setEditingLine] = useState<ITrackedLine | null>(null);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    // Removal mirrors adding: a menu with the same three ways in.
    const [removeMenuAnchor, setRemoveMenuAnchor] = useState<null | HTMLElement>(null);
    const [removeUploadOpen, setRemoveUploadOpen] = useState(false);
    const [removePasteOpen, setRemovePasteOpen] = useState(false);
    const [removeFormOpen, setRemoveFormOpen] = useState(false);
    const [pasteFileOpen, setPasteFileOpen] = useState(false);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [historyLine, setHistoryLine] = useState<ITrackedLine | null>(null);
    const [updateList, setUpdateList] = useState<IUpdateList | null>(null);
    const [checkResult, setCheckResult] = useState<ICheckResult | null>(null);

    const { data: publisher, isLoading: loadingPublisher, isError } = useQuery(
        ['trackedPublisher', id],
        () => fetchPublisher(id),
        { refetchOnWindowFocus: false },
    );

    const { data: lines, isLoading: loadingLines } = useQuery(['trackedLines', id], () => fetchLines(id), {
        enabled: !!id,
        refetchOnWindowFocus: false,
    });

    const refresh = () => {
        queryClient.invalidateQueries(['trackedLines', id]);
        queryClient.invalidateQueries(['trackedPublisher', id]);
        queryClient.invalidateQueries('trackedPublishers');
    };

    // errorMessage unwraps the server's own text; err.message alone gives "Request failed with
    // status code 409" and loses the explanation.
    const fail = (err: any) => setAlert({ msg: errorMessage(err), type: AlertTypes.FAILURE });
    const ok = (msg: string) => setAlert({ msg, type: AlertTypes.SUCCESS });

    const checkMutation = useMutation(() => checkPublisher(id), {
        onSuccess: (result) => {
            setCheckResult(result);
            refresh();
        },
        onError: fail,
    });

    const checkFromTextMutation = useMutation((content: string) => checkPublisherFromText(id, content), {
        onSuccess: (result) => {
            setCheckResult(result);
            refresh();
        },
        onError: fail,
    });

    const removeMutation = useMutation(
        ({ lineId, isRemoved }: { lineId: string; isRemoved: boolean }) => setLineRemoved(lineId, isRemoved),
        {
            // A brief confirmation rather than a dialog: the row moving tabs is easy to miss,
            // but it doesn't warrant interrupting anyone.
            onSuccess: (_data, variables) => {
                ok(
                    variables.isRemoved
                        ? 'Moved to Old lines.'
                        : 'Line restored, with its history.',
                );
                refresh();
            },
            onError: fail,
        },
    );

    const purgeMutation = useMutation(
        (selection: { publisherId: string; ids: string[] }) => purgeRemovedLines(selection.publisherId, selection.ids),
        {
            onSuccess: (r, selection) => {
                setAlert({ msg: `Deleted ${r.purged} old line(s) permanently.`, type: AlertTypes.SUCCESS });
                setOldSelection((current) => current.publisherId === selection.publisherId
                    ? { publisherId: current.publisherId, ids: [] } : current);
                setDeleteConfirmation(null);
                queryClient.invalidateQueries(['trackedLines', selection.publisherId]);
                queryClient.invalidateQueries(['trackedPublisher', selection.publisherId]);
                queryClient.invalidateQueries('trackedPublishers');
                queryClient.invalidateQueries('oldLines');
                queryClient.invalidateQueries('distinctLineSpecs');
            },
            onError: fail,
        },
    );

    const openUpdateList = async () => {
        try {
            setUpdateList(await fetchUpdateList(id));
        } catch (e) {
            fail(e);
        }
    };

    const openAppAdsTxt = () => {
        const target = resolveAppAdsTxtUrl(publisher);
        if (!target) return;
        // noopener/noreferrer: the opened page is a third party's, and shouldn't get a handle
        // on the window that opened it.
        window.open(target, '_blank', 'noopener,noreferrer');
    };

    /**
     * Inline edit only makes sense on the fields ad-ops maintain by hand. This is a whitelist:
     * anything not named here is silently dropped on save, so a new editable column has to be
     * added in both places.
     *
     * sourceDescription is deliberately absent - the generator owns it, and the Demand column
     * only falls back to displaying it. Editing that cell writes `demand`.
     */
    const processRowUpdate = async (newRow: any) => {
        await updateLine(newRow._id, {
            domain: newRow.domain,
            pubId: newRow.pubId,
            relationship: String(newRow.relationship || '').toUpperCase(),
            certId: newRow.certId,
            demand: newRow.demand,
        });
        refresh();
        return newRow;
    };

    const lineColumns: GridColDef[] = useMemo(
        () => [
            { field: 'domain', headerName: 'Ad system domain', width: 200, editable: true },
            { field: 'pubId', headerName: 'Pub ID', width: 190, editable: true },
            { field: 'relationship', headerName: 'Relationship', width: 130, editable: true },
            { field: 'certId', headerName: 'Cert ID', width: 150, editable: true },
            {
                field: 'status',
                headerName: 'Status',
                width: 210,
                renderCell: (params: any) => <StatusChip line={params.row} />,
            },
            {
                field: 'demand',
                headerName: 'Demand',
                width: 160,
                editable: true,
                /**
                 * One column for two fields. `demand` is ad-ops' own note; when it's unset we
                 * show the generator's sourceDescription instead, muted, because that is
                 * usually the same answer already known ("this one's for Wurl") and starting
                 * from blank would throw it away. Muted so it reads as inferred rather than
                 * confirmed; editing the cell always writes `demand` and never touches it.
                 */
                renderCell: (params: any) => {
                    if (params.value) return params.value;
                    const inherited = params.row.sourceDescription;
                    if (!inherited) return '';
                    return (
                        <Tooltip title="From the generator - not confirmed by anyone yet">
                            <Typography
                                variant="body2"
                                color="text.disabled"
                                fontStyle="italic"
                            >
                                {inherited}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                field: 'statusChangedAt',
                headerName: 'Last changed',
                width: 180,
                valueGetter: (params: any) =>
                    params.row.statusChangedAt ? dateToStringFormat(params.row.statusChangedAt) : 'no change',
            },
            {
                field: 'lastCheckedAt',
                headerName: 'Last checked',
                width: 180,
                valueGetter: (params: any) =>
                    params.row.lastCheckedAt ? dateToStringFormat(params.row.lastCheckedAt) : 'never',
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: 'actions',
                width: 140,
                renderCell: (params: any) => (
                    <Box>
                        <Tooltip title="Change log">
                            <IconButton size="small" onClick={() => setHistoryLine(params.row)}>
                                <HistoryIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setEditingLine(params.row);
                                    setLineFormOpen(true);
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Move to Old lines - we keep checking whether it's still live">
                            <IconButton
                                size="small"
                                onClick={() => removeMutation.mutate({ lineId: params.row._id, isRemoved: true })}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        [removeMutation],
    );

    const removedColumns: GridColDef[] = useMemo(
        () => [
            { field: 'domain', headerName: 'Ad system domain', width: 200 },
            { field: 'pubId', headerName: 'Pub ID', width: 190 },
            { field: 'relationship', headerName: 'Relationship', width: 130 },
            {
                field: 'demand',
                headerName: 'Demand',
                width: 160,
                valueGetter: (params: any) => params.row.demand || params.row.sourceDescription || '',
            },
            {
                field: 'foundInFile',
                headerName: 'Still live?',
                width: 230,
                renderCell: (params: any) => <RemovalStatusChip line={params.row} />,
            },
            {
                field: 'removedAt',
                headerName: 'Removed at',
                width: 180,
                valueGetter: (params: any) => (params.row.removedAt ? dateToStringFormat(params.row.removedAt) : '-'),
            },
            {
                field: 'lastCheckedAt',
                headerName: 'Last checked',
                width: 180,
                valueGetter: (params: any) =>
                    params.row.lastCheckedAt ? dateToStringFormat(params.row.lastCheckedAt) : 'never',
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: 'actions',
                width: 120,
                renderCell: (params: any) => (
                    <Tooltip title="Restore to Lines, keeping its history">
                        <Button
                            size="small"
                            onClick={() => removeMutation.mutate({ lineId: params.row._id, isRemoved: false })}
                        >
                            Restore
                        </Button>
                    </Tooltip>
                ),
            },
        ],
        [removeMutation],
    );

    /** Activity: every recorded transition across this publisher's lines, newest first. */
    const activity = useMemo(() => {
        const all = [...(lines?.active || []), ...(lines?.removed || [])];
        const rows = [];
        all.forEach((line) => {
            (line.statusHistory || []).forEach((h, i) => {
                rows.push({
                    id: `${line._id}-${i}`,
                    date: h.date,
                    line: `${line.domain}, ${line.pubId}, ${line.relationship}`,
                    demand: line.demand || line.sourceDescription || '',
                    from: h.from,
                    to: h.to,
                });
            });
        });
        return rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [lines]);

    const activityColumns: GridColDef[] = [
        {
            field: 'date',
            headerName: 'When',
            width: 190,
            valueGetter: (params: any) => dateToStringFormat(params.row.date),
        },
        { field: 'line', headerName: 'Line', width: 380 },
        { field: 'demand', headerName: 'Demand', width: 150 },
        {
            field: 'change',
            headerName: 'Change',
            width: 260,
            renderCell: (params: any) => {
                const { from, to } = params.row;
                const fixed = to === 'correct';
                const broke = from === 'correct' && to !== 'correct';
                return (
                    <Chip
                        size="small"
                        variant="outlined"
                        color={fixed ? 'success' : broke ? 'error' : 'warning'}
                        label={`${from ?? 'unverified'} → ${to ?? 'unverified'}`}
                    />
                );
            },
        },
    ];

    if (loadingPublisher) return <Spinner />;
    if (isError || !publisher) return <>Could not find that tracked publisher.</>;

    const busy = checkMutation.isLoading || checkFromTextMutation.isLoading;

    return (
        <Box>
            {/* Back sits on the breadcrumb row rather than a line of its own - it's one small
                control and a dedicated row for it pushed the whole page down. */}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Leap4Breadcrumbs
                    breadcrumbs={[
                        { text: 'Publisher Lines', link: urlService.appAdsLines(), icon: <TocIcon /> },
                        { text: publisher.name, icon: <TocIcon /> },
                    ]}
                />
                <BackButton to={urlService.appAdsLines()} />
            </Box>

            {/* "Update all" still sits in the tab bar's action slot on the list screens, because
                it acts on the whole seat. "Check now" acts on this publisher, so it belongs with
                the publisher's own buttons rather than beside the seat-wide tabs. */}
            <AppAdsLinesTabs />

            {/* Centred, and clear of the tabs above - left-aligned it read as though the tab bar
                were cutting into it. */}
            <Box textAlign="center" my={3}>
                <Typography variant="h5">{publisher.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {publisher.domain}
                    {publisher.contactEmail ? ` · ${publisher.contactEmail}` : ''}
                    {` · responsiveness ${publisher.responsiveness ?? 3}/5`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {publisher.lastCheckedAt
                        ? `Last checked ${dateToStringFormat(publisher.lastCheckedAt)}`
                        : 'Never checked'}
                </Typography>
                {!!publisher.accountManagers?.length && (
                    <Typography variant="body2" color="text.secondary">
                        {publisher.accountManagers.join(', ')}
                    </Typography>
                )}
            </Box>

            {publisher.lastCheckError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The last check couldn't read this publisher's file: {publisher.lastCheckError}. Some publishers sit
                    behind bot protection — use "Paste file instead" with what you can see in your own browser.
                </Alert>
            )}

            {busy && <LinearProgress sx={{ mb: 2 }} />}

            {/* Add and remove offer the same three ways in, in the same order, so the two
                menus read as one pair rather than two unrelated features. Generation is a
                fourth way lines arrive and has no counterpart, so it sits after a divider. */}
            <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor} onClose={() => setAddMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        setAddMenuAnchor(null);
                        setEditingLine(null);
                        setLineFormOpen(true);
                    }}
                >
                    Write a line
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setAddMenuAnchor(null);
                        setPasteOpen(true);
                    }}
                >
                    Paste lines
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setAddMenuAnchor(null);
                        setUploadOpen(true);
                    }}
                >
                    Upload a file
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => {
                        setAddMenuAnchor(null);
                        setGenerateOpen(true);
                    }}
                >
                    Generate from buyers
                </MenuItem>
            </Menu>

            <Menu anchorEl={removeMenuAnchor} open={!!removeMenuAnchor} onClose={() => setRemoveMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        setRemoveMenuAnchor(null);
                        setRemoveFormOpen(true);
                    }}
                >
                    Write a line
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setRemoveMenuAnchor(null);
                        setRemovePasteOpen(true);
                    }}
                >
                    Paste lines
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setRemoveMenuAnchor(null);
                        setRemoveUploadOpen(true);
                    }}
                >
                    Upload a file
                </MenuItem>
            </Menu>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
                <Tab label={`Lines (${lines?.active?.length ?? 0})`} />
                <Tab label={`Old lines (${lines?.removed?.length ?? 0})`} />
                <Tab label={`Activity (${activity.length})`} />
            </Tabs>

            {/* One band immediately above the table: what applies to the current tab on the
                left, what applies to the publisher on the right. */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mb={1}
            >
                {/* Add, remove, then the list to send - the order the work actually happens in.
                    Add and remove are filled because they're what people came here to do; the
                    rest are outlined so the two first choices stand out.

                    Contained-error on Remove signals direction, not permanence: removing moves
                    lines to Old lines, where a check can still confirm whether the publisher
                    took them out, and restoring one brings its history back with it. */}
                <Box display="flex" gap={1} flexWrap="wrap">
                    {tab === 0 && (
                        <>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                            >
                                Add lines
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={<RemoveIcon />}
                                onClick={(e) => setRemoveMenuAnchor(e.currentTarget)}
                            >
                                Remove lines
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={openUpdateList}
                            >
                                Generate updated publisher list
                            </Button>
                        </>
                    )}
                    {tab === 1 && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            disabled={!selectedOldIds.length || purgeMutation.isLoading}
                            onClick={() => setDeleteConfirmation({ publisherId: id, ids: [...selectedOldIds] })}
                        >
                            Delete selected ({selectedOldIds.length})
                        </Button>
                    )}
                </Box>

                <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<RefreshIcon />}
                        disabled={busy}
                        onClick={() => checkMutation.mutate()}
                    >
                        Check now
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setPasteFileOpen(true)}>
                        Add app-ads.txt file manually
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={openAppAdsTxt}>
                        Go to
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => history.push(urlService.editTrackedPublisher(id))}
                    >
                        Publisher settings
                    </Button>
                </Box>
            </Box>

            {tab === 0 && (
                <>
                    <DataGridPro
                        {...lineWrapping.gridProps}
                        disableColumnPinning
                        rows={lines?.active || []}
                        columns={lineColumns}
                        loading={loadingLines}
                        autoHeight
                        getRowId={(row) => row._id}
                        pageSize={100}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableSelectionOnClick
                        editMode="row"
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={fail}
                        showToolbar
                        slots={{ toolbar: FiltersToolbar }}
                        slotProps={{
                            toolbar: {
                                // Exports whatever the grid currently shows (filtered + sorted),
                                // but only the four fields that make up an app-ads.txt line.
                                csvOptions: {
                                    fields: ['domain', 'pubId', 'relationship', 'certId'],
                                    fileName: `${publisher?.name || 'publisher'}-lines`,
                                },
                            },
                        }}
                        style={{ minHeight: '50vh' }}
                    />
                </>
            )}

            {tab === 1 && (
                <>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        Lines you've asked the publisher to remove. They stay here until a check confirms they're gone,
                        because until then we don't know whether they're still live.
                    </Alert>
                    <DataGridPro
                        {...removedWrapping.gridProps}
                        disableColumnPinning
                        rows={lines?.removed || []}
                        checkboxSelection
                        checkboxSelectionVisibleOnly
                        disableRowSelectionExcludeModel
                        pagination
                        rowSelectionModel={selectedOldIds}
                        onRowSelectionModelChange={(ids) => {
                            if (!purgeMutation.isLoading) setOldSelection({ publisherId: id, ids: ids.map(String) });
                        }}
                        disableRowSelectionOnClick
                        columns={removedColumns}
                        loading={loadingLines}
                        autoHeight
                        getRowId={(row) => row._id}
                        pageSize={100}
                        rowsPerPageOptions={[25, 50, 100]}
                        style={{ minHeight: '50vh' }}
                    />
                </>
            )}

            <Dialog open={deleteConfirmation?.publisherId === id} onClose={() => {
                if (!purgeMutation.isLoading) setDeleteConfirmation(null);
            }}>
                <DialogTitle>Delete selected old lines?</DialogTitle>
                <DialogContent>
                    Permanently delete these {deleteConfirmation?.ids.length ?? 0} old lines and their history?
                    This cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button disabled={purgeMutation.isLoading} onClick={() => setDeleteConfirmation(null)}>Cancel</Button>
                    <Button color="error" disabled={purgeMutation.isLoading || !deleteConfirmation?.ids.length}
                        onClick={() => { if (deleteConfirmation) purgeMutation.mutate(deleteConfirmation); }}>
                        {purgeMutation.isLoading ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            {tab === 2 && (
                <DataGridPro
                    {...activityWrapping.gridProps}
                    disableColumnPinning
                    rows={activity}
                    columns={activityColumns}
                    loading={loadingLines}
                    autoHeight
                    getRowId={(row) => row.id}
                    pageSize={100}
                    rowsPerPageOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    style={{ minHeight: '50vh' }}
                />
            )}

            {/* ---------- modals ---------- */}

            <LineFormModal
                open={lineFormOpen}
                line={editingLine}
                onClose={() => {
                    setLineFormOpen(false);
                    setEditingLine(null);
                }}
                onSave={async (row) => {
                    if (editingLine?._id) {
                        await updateLine(editingLine._id, row);
                        ok('Line updated.');
                    } else {
                        const result = await createLine(id, row);
                        if (result && result.added === 0) {
                            throw new Error('This publisher already has that exact line (same domain, ID and relationship).');
                        }
                        ok('Line added.');
                    }
                    refresh();
                }}
            />

            <PasteLinesModal
                open={pasteOpen}
                onPreview={async (text) => {
                    const p = await previewLinesText(id, text);
                    return {
                        rowsRead: p.rowsRead,
                        understood: p.wouldAdd.length + p.wouldSkip,
                        issues: p.issues || [],
                        detail: `${p.wouldAdd.length} would be added, ${p.wouldSkip} already present.`,
                    };
                }}
                onClose={() => {
                    setPasteOpen(false);
                    refresh();
                }}
                onSubmit={(text) => importLinesFromText(id, text)}
            />

            <UploadRowsModal
                open={uploadOpen}
                title="Upload lines to track"
                helperText="CSV or Excel listing the lines this publisher should have. Either one column holding the whole line (pubmatic.com, 161599, DIRECT) or separate Domain / Pub ID / Relationship / Cert ID columns — both are detected automatically, and a 'Domain' or 'Line' header row is skipped. Every row has to be readable: if any row isn't, nothing is imported and you'll see exactly which to fix. Lines already tracked are skipped."
                onPreview={async (rows) => {
                    const p = await previewLinesCsv(id, rows);
                    return {
                        rowsRead: p.rowsRead,
                        understood: p.wouldAdd.length + p.wouldSkip,
                        issues: p.issues || [],
                    };
                }}
                onClose={() => {
                    setUploadOpen(false);
                    refresh();
                }}
                onSubmit={(rows) => importLinesFromCsv(id, rows)}
            />

            <UploadRowsModal
                open={removeUploadOpen}
                title="Remove lines — upload a file"
                actionLabel="Remove"
                helperText="Upload your own CSV or Excel list of lines to stop tracking — for example a list a publisher sent you. Domain and Pub ID are required; relationship is optional, and leaving it out matches the line however it's listed. Every row has to be readable, or nothing is removed. Matching lines move to Old lines rather than being deleted, so we keep checking whether they're still live."
                onPreview={async (rows) => {
                    const p = await previewDeleteCsv(id, rows);
                    return { rowsRead: p.rowsRead, understood: p.lookups.length, issues: (p as any).issues || [] };
                }}
                onClose={() => {
                    setRemoveUploadOpen(false);
                    refresh();
                }}
                onSubmit={(rows) => massDeleteFromCsv(id, rows)}
            />

            {/* The same component the add path uses, pointed at the removal endpoints. One
                implementation means a badly formed line is refused identically either way. */}
            <PasteLinesModal
                open={removePasteOpen}
                title="Remove lines — paste"
                actionLabel="Remove"
                helperText="One line per row. Domain and publisher ID are required; the relationship is optional, and leaving it out matches the line however it's listed. Every row has to be readable, or nothing is removed. Matching lines move to Old lines rather than being deleted, so checks keep telling you whether they're still live."
                placeholder={'pubmatic.com, 161599\nconversantmedia.com, 100353, RESELLER'}
                onPreview={async (text) => {
                    const p = await previewDeleteText(id, text);
                    return {
                        rowsRead: p.rowsRead,
                        understood: p.lookups.length,
                        issues: p.issues || [],
                        detail: `${p.lookups.length} would be looked up.`,
                    };
                }}
                describeResult={(r) =>
                    `Moved ${r.removed} line(s) to Old lines.` +
                    (r.notFound ? ` ${r.notFound} weren't tracked here, so nothing to remove.` : '')
                }
                onClose={() => {
                    setRemovePasteOpen(false);
                    refresh();
                }}
                onSubmit={(text) => massDeleteFromText(id, text)}
            />

            {/* Removal's "write a line", the counterpart of the add form. Relationship is
                optional here and cert ID is hidden, because neither identifies the line being
                looked up - cert ID plays no part in matching at all. */}
            <LineFormModal
                open={removeFormOpen}
                title="Remove a line"
                actionLabel="Remove"
                relationshipOptional
                hideCertId
                hideDemand
                onClose={() => setRemoveFormOpen(false)}
                onSave={async (row) => {
                    const text = [row.domain, row.pubId, row.relationship].filter(Boolean).join(', ');
                    const result = await massDeleteFromText(id, text);
                    if (!result.removed) {
                        throw new Error(
                            "This publisher isn't tracking that line, so there's nothing to remove.",
                        );
                    }
                    ok(`Moved ${result.removed} line(s) to Old lines.`);
                    refresh();
                }}
            />

            <ManualCheckModal
                open={pasteFileOpen}
                onClose={() => setPasteFileOpen(false)}
                onSubmit={async (text) => {
                    await checkFromTextMutation.mutateAsync(text);
                }}
            />

            <LineHistoryModal open={!!historyLine} line={historyLine} onClose={() => setHistoryLine(null)} />

            <UpdateListModal
                open={!!updateList}
                list={updateList}
                onClose={() => setUpdateList(null)}
                onDownloadCsv={() => downloadUpdateListCsv(id, publisher.name).then(refresh).catch(fail)}
            />

            <GenerateLinesModal
                open={generateOpen}
                publisherId={id}
                publisherName={publisher.name}
                onClose={() => setGenerateOpen(false)}
                onSaved={(result) => {
                    ok(describeSaveResult(result));
                    refresh();
                }}
            />

            <Dialog open={!!checkResult} onClose={() => setCheckResult(null)} fullWidth maxWidth="sm">
                <DialogTitle>{checkResult?.ok ? 'Check finished' : 'Could not read the file'}</DialogTitle>
                <DialogContent dividers>
                    {checkResult?.ok ? (
                        <>
                            <Typography variant="body2" gutterBottom>
                                Read {checkResult.parsedEntryCount} line(s) from the live file.
                            </Typography>
                            {checkResult.currentCounts && (
                                <Typography variant="body2" gutterBottom>
                                    Of your {checkResult.currentCounts.correct +
                                        checkResult.currentCounts.incorrect +
                                        checkResult.currentCounts.missing +
                                        checkResult.currentCounts.unverified}{' '}
                                    tracked line(s): {checkResult.currentCounts.correct} correct,{' '}
                                    {checkResult.currentCounts.incorrect} wrong type,{' '}
                                    {checkResult.currentCounts.missing} not found.
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                {checkResult.summary}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" gutterBottom>
                                {checkResult?.summary}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {checkResult?.error}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCheckResult(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default connector(TrackedPublisherDetail);
